/** ContactHQ live subscription plans (matches product pricing UI) */

const SHARED_FEATURES = [
  "Unlimited Responses",
  "Export to CSV/Excel",
  "Advanced Analytics",
  "Remove Branding",
  "Priority Support",
];

export const PLAN_CATALOG = [
  {
    id: "Free",
    label: "Free",
    formLimit: 1,
    billingCycle: "monthly",
    price: 0,
    priceLabel: "₹0",
    description: "Default trial tier",
    features: ["1 Active Project", ...SHARED_FEATURES.slice(0, 2)],
  },
  {
    id: "Platform Supporter",
    label: "Platform Supporter",
    formLimit: 2,
    billingCycle: "monthly",
    price: 49,
    priceLabel: "₹49 /month",
    yearlyPrice: 490,
    yearlyPriceLabel: "₹490 /year",
    yearlyStrike: 588,
    icon: "volunteer_activism",
    accent: "green",
    description: "2 active projects",
    features: ["2 Active Projects", ...SHARED_FEATURES],
  },
  {
    id: "Core Sponsor",
    label: "Core Sponsor",
    formLimit: 5,
    billingCycle: "yearly",
    price: 99,
    priceLabel: "₹99 /month",
    yearlyPrice: 990,
    yearlyPriceLabel: "₹990 /year",
    yearlyStrike: 1188,
    icon: "workspace_premium",
    accent: "rose",
    description: "5 active projects",
    features: ["5 Active Projects", ...SHARED_FEATURES],
  },
  {
    id: "Platform Patron",
    label: "Platform Patron",
    formLimit: 10,
    billingCycle: "yearly",
    price: 149,
    priceLabel: "₹149 /month",
    yearlyPrice: 1490,
    yearlyPriceLabel: "₹1490 /year",
    yearlyStrike: 1788,
    icon: "auto_awesome",
    accent: "violet",
    description: "10 active projects",
    features: ["10 Active Projects", ...SHARED_FEATURES],
  },
];

/** One-select options: plan + billing cycle combined */
export const PLAN_SELECT_OPTIONS = [
  {
    value: "Free|monthly",
    planId: "Free",
    billingCycle: "monthly",
    label: "Free — 1 project · Monthly",
  },
  {
    value: "Platform Supporter|monthly",
    planId: "Platform Supporter",
    billingCycle: "monthly",
    label: "Platform Supporter — ₹49/mo · 2 projects",
  },
  {
    value: "Platform Supporter|yearly",
    planId: "Platform Supporter",
    billingCycle: "yearly",
    label: "Platform Supporter — ₹490/yr · 2 projects",
  },
  {
    value: "Core Sponsor|monthly",
    planId: "Core Sponsor",
    billingCycle: "monthly",
    label: "Core Sponsor — ₹99/mo · 5 projects",
  },
  {
    value: "Core Sponsor|yearly",
    planId: "Core Sponsor",
    billingCycle: "yearly",
    label: "Core Sponsor — ₹990/yr · 5 projects",
  },
  {
    value: "Platform Patron|monthly",
    planId: "Platform Patron",
    billingCycle: "monthly",
    label: "Platform Patron — ₹149/mo · 10 projects",
  },
  {
    value: "Platform Patron|yearly",
    planId: "Platform Patron",
    billingCycle: "yearly",
    label: "Platform Patron — ₹1490/yr · 10 projects",
  },
];

export const BILLING_CYCLES = [
  { id: "monthly", label: "Monthly", days: 30 },
  { id: "yearly", label: "Yearly", days: 365 },
  { id: "lifetime", label: "Lifetime", days: 36500 },
];

export const getPlanById = (planId) =>
  PLAN_CATALOG.find((p) => p.id === planId) || PLAN_CATALOG[0];

export const getBillingCycleById = (cycleId) =>
  BILLING_CYCLES.find((c) => c.id === cycleId) || BILLING_CYCLES[0];

export const computeExpiryDate = (billingCycle, fromDate = new Date()) => {
  const cycle = getBillingCycleById(billingCycle);
  const date = new Date(fromDate);
  date.setDate(date.getDate() + cycle.days);
  return date.toISOString();
};

export const makePlanOptionValue = (planId, billingCycle) =>
  `${planId}|${billingCycle || "monthly"}`;

export const parsePlanOptionValue = (value) => {
  const [planId, billingCycle = "monthly"] = String(value || "").split("|");
  return { planId: planId || "Free", billingCycle };
};

/** Apply all defaults from a selected plan + billing cycle */
export const applyPlanDefaults = (planId, billingCycle = "monthly") => {
  const plan = getPlanById(planId);
  const cycle = billingCycle || plan.billingCycle || "monthly";
  const isYearly = cycle === "yearly";
  const amount = isYearly
    ? plan.yearlyPrice ?? plan.price * 12
    : plan.price ?? 0;
  const priceLabel = isYearly
    ? plan.yearlyPriceLabel || `₹${amount} /year`
    : plan.priceLabel || `₹${amount} /month`;

  return {
    plan: plan.id,
    label: plan.label,
    formLimit: plan.formLimit,
    billingCycle: cycle,
    billingLabel: getBillingCycleById(cycle).label,
    price: amount,
    priceLabel,
    features: plan.features || [],
    expiresAt: computeExpiryDate(cycle),
  };
};
